import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, ButtonGroup, Button, Spinner } from 'react-bootstrap';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { fetchDashboardSummary, fetchExpensesChart } from '../../redux/slices/dashboardSlice';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #dee2e6',
      borderRadius: 8,
      padding: '10px 14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      maxWidth: 260,
    }}>
      <p style={{ fontWeight: 600, marginBottom: 4, color: '#495057' }}>
        {format(parseISO(label), 'dd MMM yyyy', { locale: es })}
      </p>
      <p style={{ color: '#dc3545', marginBottom: data.expenses?.length ? 8 : 0 }}>
        Total: <strong>${data.total.toFixed(2)}</strong>
      </p>
      {data.expenses?.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem', color: '#6c757d' }}>
          {data.expenses.map((e, i) => (
            <li key={i}>
              {e.description} — <strong>${e.amount.toFixed(2)}</strong>
              {e.category && <span style={{ color: '#adb5bd' }}> ({e.category})</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const { summary, chartData, loading, chartLoading } = useSelector((state) => state.dashboard);
  const { startDate, endDate } = useSelector((state) => state.period);
  const [chartDays, setChartDays] = useState(30);

  const hasPeriod = startDate && endDate;

  useEffect(() => {
    if (hasPeriod) {
      dispatch(fetchDashboardSummary({ startDate, endDate }));
      dispatch(fetchExpensesChart({ startDate, endDate }));
    } else {
      dispatch(fetchDashboardSummary());
      dispatch(fetchExpensesChart({ days: chartDays }));
    }
  }, [dispatch, startDate, endDate]);

  const handleDaysChange = (days) => {
    setChartDays(days);
    dispatch(fetchExpensesChart({ days }));
  };

  const balance = (summary?.total_income ?? 0) - (summary?.total_expenses ?? 0);

  // Only show days that have at least one expense, or keep all for the line shape
  const chartPoints = chartData.filter(d => d.total > 0);
  const hasChartData = chartPoints.length > 0;

  if (loading) {
    return <Container className="mt-4"><div className="text-center">Cargando...</div></Container>;
  }

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Dashboard Financiero</h2>

      {/* Summary cards */}
      <Row>
        <Col md={3} className="mb-3">
          <Card bg="success" text="white">
            <Card.Body>
              <Card.Title>Ingresos Totales</Card.Title>
              <h3>${summary?.total_income?.toFixed(2) || '0.00'}</h3>
              <small>{summary?.incomes_count || 0} registros</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card bg="danger" text="white">
            <Card.Body>
              <Card.Title>Gastos Fijos</Card.Title>
              <h3>${summary?.total_fixed_expenses?.toFixed(2) || '0.00'}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card bg="warning" text="white">
            <Card.Body>
              <Card.Title>Gastos Variables</Card.Title>
              <h3>${summary?.total_variable_expenses?.toFixed(2) || '0.00'}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card bg={balance >= 0 ? 'info' : 'danger'} text="white">
            <Card.Body>
              <Card.Title>Balance</Card.Title>
              <h3>${balance.toFixed(2)}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Expenses chart */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title className="mb-0">Trazabilidad de Egresos</Card.Title>
            {hasPeriod ? (
              <small className="text-muted">{startDate} → {endDate}</small>
            ) : (
              <ButtonGroup size="sm">
                {[30, 60, 90].map(d => (
                  <Button
                    key={d}
                    variant={chartDays === d ? 'danger' : 'outline-danger'}
                    onClick={() => handleDaysChange(d)}
                  >
                    {d} días
                  </Button>
                ))}
              </ButtonGroup>
            )}
          </div>

          {chartLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : !hasChartData ? (
            <div className="text-center text-muted py-5">
              No hay egresos registrados en los últimos {chartDays} días
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc3545" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#dc3545" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => format(parseISO(d), 'dd/MM', { locale: es })}
                  tick={{ fontSize: 11 }}
                  interval={Math.floor(chartData.length / 8)}
                />
                <YAxis
                  tickFormatter={(v) => `$${v}`}
                  tick={{ fontSize: 11 }}
                  width={60}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#dc3545"
                  strokeWidth={2}
                  fill="url(#expenseGradient)"
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    if (payload.total === 0) return null;
                    return (
                      <circle
                        key={`dot-${payload.date}`}
                        cx={cx} cy={cy} r={4}
                        fill="#dc3545" stroke="#fff" strokeWidth={2}
                      />
                    );
                  }}
                  activeDot={{ r: 6, fill: '#dc3545', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card.Body>
      </Card>

      {/* Bottom summary panels */}
      <Row className="mt-2">
        <Col md={6} className="mb-3">
          <Card>
            <Card.Body>
              <Card.Title>Resumen de Gastos</Card.Title>
              <div className="mt-3">
                <p><strong>Total Gastos:</strong> ${summary?.total_expenses?.toFixed(2) || '0.00'}</p>
                <p><strong>Gastos Fijos:</strong> ${summary?.total_fixed_expenses?.toFixed(2) || '0.00'}</p>
                <p><strong>Gastos Variables:</strong> ${summary?.total_variable_expenses?.toFixed(2) || '0.00'}</p>
                <p><strong>Total Registros:</strong> {summary?.expenses_count || 0}</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-3">
          <Card>
            <Card.Body>
              <Card.Title>Microcréditos</Card.Title>
              <div className="mt-3">
                <p><strong>Pagos Activos:</strong> {summary?.active_payments || 0}</p>
                <p><strong>Cuotas Pendientes:</strong> {summary?.pending_installments_count || 0}</p>
                <p><strong>Deuda Pendiente:</strong> ${summary?.total_pending_debt?.toFixed(2) || '0.00'}</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
